import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Coffee, Shield, BookOpen, Download, Archive, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Language } from '../App';
import { PageHero } from '../components/PageHero';
interface SubscriptionProps {
  language: Language;
}
export function Subscription({
  language
}: SubscriptionProps) {
  const [supportAmount, setSupportAmount] = useState<5 | 10>(5);
  const isPashto = language === 'pashto';
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px' as const
    },
    transition: {
      duration: 0.6
    }
  };
  const benefits = isPashto ? [{
    icon: BookOpen,
    text: 'بشپړو مجلو ته لاسرسی'
  }, {
    icon: Download,
    text: 'ګڼې په PDF بڼه ډاونلوډ کړئ'
  }, {
    icon: Archive,
    text: 'بشپړ آرشیف وپلټئ'
  }] : [{
    icon: BookOpen,
    text: 'Access full magazine issues'
  }, {
    icon: Download,
    text: 'Download editions in PDF format'
  }, {
    icon: Archive,
    text: 'Explore the full archive'
  }];
  return <div className="pt-24 md:pt-28 bg-gray-50 min-h-screen pb-8" data-id="element-651">
      <PageHero language={language} titleEn="Support Pasoon" titlePs="د پاڅون ملاتړ وکړئ" introEn="Preserve Pashto language and literature" introPs="د پښتو ژبه او ادب وساتئ" data-id="element-652" />

      {/* Intro */}
      <section className="py-8 px-6" data-id="element-653">
        <div className="max-w-3xl mx-auto text-center" data-id="element-654">
          <motion.div {...fadeIn} data-id="element-655">
            <p className={`text-gray-800 font-medium ${isPashto ? 'font-pashto leading-[2.2] text-lg md:text-xl' : 'text-xl md:text-2xl leading-relaxed'}`} data-id="element-656">
              {isPashto ? 'پاڅون یوازې یوه مجله نه ده. دا د پښتو ژبې او کلتور د ساتنې پلیټفارم دی. ستاسو مرسته موږ سره مرسته کوي چې خپرونو ته دوام ورکړو، لیکوالانو ته ملاتړ وکړو، او خپله رسیدنه پراخه کړو.' : 'Pasoon is more than a magazine. It is a platform for preserving Pashto language and culture. Your support helps us continue publishing, supporting writers, and expanding our reach.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-8 px-6" data-id="element-657">
        <div className="max-w-5xl mx-auto" data-id="element-658">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-659">
            {/* Subscription Card - Primary */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative bg-white rounded-3xl shadow-2xl shadow-pasoon-red/10 border-2 border-pasoon-red/20 overflow-hidden flex flex-col" data-id="element-660">
              <div className="h-3 bg-pasoon-red w-full absolute top-0 left-0" data-id="element-661"></div>

              <div className="p-8 md:p-12 flex-grow flex flex-col" data-id="element-662">
                <h3 className={`text-3xl md:text-4xl font-bold text-pasoon-dark mb-8 text-center ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-663">
                  {isPashto ? 'ګډون' : 'Subscription'}
                </h3>

                {/* Price */}
                <div className="flex justify-center items-center mb-10" data-id="element-664">
                  <span className="text-7xl md:text-8xl font-serif font-extrabold text-pasoon-red tracking-tight" data-id="element-665">
                    $5
                  </span>
                </div>

                {/* Benefits */}
                <ul className="space-y-5 mb-12 flex-grow" data-id="element-666">
                  {benefits.map((b, idx) => <li key={idx} className="flex items-center gap-4" data-id="element-667">
                      <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0" data-id="element-668">
                        <b.icon className="w-6 h-6 text-pasoon-red" data-id="element-669" />
                      </div>
                      <span className={`text-lg text-gray-800 font-medium ${isPashto ? 'font-pashto' : ''}`} data-id="element-670">
                        {b.text}
                      </span>
                    </li>)}
                </ul>

                {/* CTA */}
                <div className="mt-auto" data-id="element-671">
                  <button className={`w-full flex items-center justify-center gap-3 px-8 py-6 bg-pasoon-red text-white rounded-2xl font-bold text-xl hover:bg-red-800 transition-all shadow-xl shadow-red-900/20 hover:shadow-red-900/40 hover:-translate-y-1 active:translate-y-0 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-672">
                    <Mail className="w-6 h-6" data-id="element-673" />
                    {isPashto ? 'اوس ګډون وکړئ' : 'Subscribe Now'}
                  </button>

                  <div className={`flex items-center justify-center gap-2 mt-6 text-gray-500 text-sm font-medium ${isPashto ? 'font-pashto' : ''}`} data-id="element-674">
                    <Shield className="w-4 h-4" data-id="element-675" />
                    {isPashto ? 'د PayPal له لارې خوندي تادیات' : 'Secure payments via PayPal'}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Support Card - Secondary */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.15
          }} className="relative bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-200 overflow-hidden flex flex-col" data-id="element-676">
              <div className="h-2 bg-pasoon-muted w-full absolute top-0 left-0" data-id="element-677"></div>

              <div className="p-8 md:p-12 flex-grow flex flex-col" data-id="element-678">
                <h3 className={`text-2xl md:text-3xl font-bold text-pasoon-dark mb-4 text-center ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-679">
                  {isPashto ? 'د مدیرانو سره مرسته' : 'Support the Editors'}
                </h3>

                <p className={`text-gray-600 text-center mb-10 text-lg ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-680">
                  {isPashto ? 'که تاسو دې کار ته ارزښت ورکوئ، تاسو کولای شئ مستقیماً د مدیرانو سره مرسته وکړئ.' : 'If you value this work, you can support the editors directly.'}
                </p>

                {/* Amount Selection */}
                <div className="mb-10 flex-grow" data-id="element-681">
                  <div className="flex gap-4" data-id="element-682">
                    <button onClick={() => setSupportAmount(5)} className={`flex-1 py-6 rounded-2xl font-serif text-4xl font-bold border-2 transition-all ${supportAmount === 5 ? 'border-pasoon-muted bg-green-50 text-pasoon-muted shadow-md scale-[1.02]' : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-683">
                      $5
                    </button>
                    <button onClick={() => setSupportAmount(10)} className={`flex-1 py-6 rounded-2xl font-serif text-4xl font-bold border-2 transition-all ${supportAmount === 10 ? 'border-pasoon-muted bg-green-50 text-pasoon-muted shadow-md scale-[1.02]' : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-684">
                      $10
                    </button>
                  </div>
                </div>

                <div className="mt-auto" data-id="element-685">
                  <p className={`text-center text-gray-600 mb-8 font-medium ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-686">
                    {isPashto ? 'هره مرسته د پاڅون او د هغې ماموریت دوام ته مرسته کوي.' : 'Every contribution helps sustain Pasoon and its mission.'}
                  </p>

                  {/* CTA */}
                  <button className={`w-full flex items-center justify-center gap-3 px-8 py-6 bg-pasoon-muted text-white rounded-2xl font-bold text-xl hover:bg-green-800 transition-all shadow-xl shadow-green-900/20 hover:shadow-green-900/40 hover:-translate-y-1 active:translate-y-0 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-687">
                    <Coffee className="w-6 h-6" data-id="element-688" />
                    {isPashto ? 'مرسته وکړئ' : 'Contribute'} — ${supportAmount}
                  </button>

                  <div className={`flex items-center justify-center gap-2 mt-6 text-gray-500 text-sm font-medium ${isPashto ? 'font-pashto' : ''}`} data-id="element-689">
                    <Shield className="w-4 h-4" data-id="element-690" />
                    {isPashto ? 'د PayPal له لارې خوندي تادیات' : 'Secure payments via PayPal'}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bank Details */}
      <section className="py-8 px-6" data-id="element-691">
        <div className="max-w-3xl mx-auto" data-id="element-692">
          <motion.div {...fadeIn} className="flex flex-col items-center justify-center gap-6" data-id="element-693">
            <p className={`text-gray-700 text-lg ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-694">
              {isPashto ? 'د مجلې سره د مرستې غوره لاره کلنی ګډون دی. دا د بریښنالیک له لارې د غوښتنې په لیږلو سره ترسره کیدی شي. تاسو کولای شئ د PayPal له لارې هم تادیه وکړئ.' : 'The best way to help the magazine is to have an annual subscription. This can be done by sending a request to the email below. You can also pay by PayPal account.'}
            </p>

            <div className="flex items-center gap-2 text-gray-800 font-bold text-xl bg-white px-6 py-3 rounded-full shadow-sm border border-gray-100" data-id="element-695">
              <Mail className="w-6 h-6 text-pasoon-red" data-id="element-696" />
              <a href="mailto:pasoonmardan@gmail.com" className="hover:text-pasoon-red transition-colors" data-id="element-697">
                pasoonmardan@gmail.com
              </a>
            </div>

            <div className="w-full h-px bg-gray-200 my-2" data-id="element-698"></div>

            <p className={`text-gray-600 ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-699">
              {isPashto ? 'مجله زموږ د سیمه ایز دفتر سره په اړیکه کې هم ګډون کیدی شي. جزیات د اړیکې په پاڼه کې دي.' : 'The magazine can be subscribed by contacting our local office. Details are on the contact page.'}
            </p>

            <Link to="/contact" className={`inline-flex items-center gap-2 text-pasoon-muted hover:text-green-800 font-medium transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-700">
              <MapPin className="w-5 h-5" data-id="element-701" />
              {isPashto ? 'د اړیکې پاڼه وګورئ' : 'View Contact Page'}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Local Office */}
      <section className="py-8 px-6 mb-6" data-id="element-702">
        {/* Additional content about local office or similar, if any, or placeholder if not specified in original code */}
      </section>
    </div>;
}